import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import { motion, AnimatePresence } from 'framer-motion';
import { API_URL } from '../config';
import { 
  Car, 
  Key, 
  Calendar, 
  User, 
  Send, 
  CheckCircle2, 
  ArrowRight,
  Shield,
  Clock,
  Briefcase,
  Loader2
} from 'lucide-react';
import Swal from 'sweetalert2';
import './Quote.css';

const QuoteInput = ({ label, name, type = "text", value, onChange, placeholder = " " }) => (
  <div className={`quote-input-group ${placeholder !== " " ? "has-placeholder" : ""}`}>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="quote-input"
      placeholder={placeholder}
      required
    />
    <label className="quote-input-label">{label}</label>
  </div>
);

const Quote = () => {
  const [serviceType, setServiceType] = useState('rental'); // 'rental' or 'sale'
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    startDate: '',
    duration: '',
    notes: '',
    kvkkAccepted: false
  });
  const [vehicles, setVehicles] = useState({ rental: [], sale: [] });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/vehicles`)
      .then(res => res.json())
      .then(data => {
        const rental = data.filter(v => v.type === 'Kiralık');
        const sale = data.filter(v => v.type === 'Satılık' || v.type === 'Satilik');
        setVehicles({ rental, sale });
        setLoading(false);
      })
      .catch(err => {
        console.error('Teklif formu araçları yüklenemedi:', err);
        setLoading(false);
      });
  }, []);

  // Filter vehicles based on service type
  const availableVehicles = serviceType === 'rental' ? vehicles.rental : vehicles.sale;

  useEffect(() => {
    // Reset selected vehicle when service type changes
    setSelectedVehicle(null);
  }, [serviceType]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVehicle) {
      Swal.fire({
        icon: 'warning',
        title: 'Araç Seçilmedi',
        text: 'Lütfen teklif almak istediğiniz bir aracı seçiniz.',
        background: '#0f172a',
        color: '#fff',
        confirmButtonColor: '#00d1ff'
      });
      return;
    }

    if (!formData.kvkkAccepted) {
      Swal.fire({
        icon: 'warning',
        title: 'KVKK Onayı',
        text: 'Lütfen KVKK metnini onaylayınız.',
        background: '#0f172a',
        color: '#fff',
        confirmButtonColor: '#00d1ff'
      });
      return;
    }

    setLoading(true);

    const nameParts = formData.fullName.trim().split(' ');
    const name = nameParts[0];
    const surname = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

    const payload = {
      name,
      surname,
      email: formData.email,
      phone: formData.phone,
      service_type: serviceType,
      vehicle_id: selectedVehicle.id,
      duration_budget: formData.duration,
      notes: formData.notes
    };

    try {
      const response = await fetch(`${API_URL}/quotes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Talebiniz Alındı!',
          text: 'Teklif talebiniz başarıyla bize ulaştı. Satış temsilcilerimiz en kısa sürede sizinle iletişime geçecektir.',
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00d1ff'
        }).then(() => {
          window.location.href = '/';
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: 'Talebiniz iletilirken bir sorun oluştu. Lütfen bilgilerinizi kontrol edip tekrar deneyiniz.',
          background: '#0f172a',
          color: '#fff',
          confirmButtonColor: '#00d1ff'
        });
      }
    } catch (err) {
      console.error('Quote submission error:', err);
      Swal.fire({
        icon: 'error',
        title: 'Bağlantı Hatası',
        text: 'Sunucuya bağlanılamadı. Lütfen backend sunucusunun çalıştığından ve güncel olduğundan emin olun.',
        background: '#0f172a',
        color: '#fff',
        confirmButtonColor: '#00d1ff'
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="quote-page">
      <Navbar />

      {/* Ambient Lighting */}
      <div className="ambient-light" style={{ top: '-10%', right: '-5%', opacity: 0.1, width: '60vw', height: '60vw' }}></div>
      <div className="ambient-light ambient-light-silver" style={{ bottom: '-10%', left: '-5%', opacity: 0.05, width: '50vw', height: '50vw' }}></div>

      <main className="quote-container">
        <header className="quote-header">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="quote-title"
          >
            Özel Teklif<span>Alın.</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="quote-subtitle"
          >
            Hayalinizdeki araca en uygun koşullarla ulaşmanız için size özel bir teklif hazırlayalım.
          </motion.p>
        </header>

        <form className="quote-grid" onSubmit={handleSubmit}>
          
          <div className="quote-main-content">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="quote-form-card"
            >
              {/* Step 1: Service Type */}
              <section className="quote-section">
                <div className="quote-section-label">
                  <span className="quote-section-number">01</span>
                  <h3 className="quote-section-title">Hizmet Türü</h3>
                </div>
                <div className="service-selector">
                  <div 
                    className={`service-option ${serviceType === 'rental' ? 'active' : ''}`}
                    onClick={() => setServiceType('rental')}
                  >
                    <Clock className="service-option-icon" size={24} />
                    <span className="service-option-title">Araç Kiralama</span>
                  </div>
                  <div 
                    className={`service-option ${serviceType === 'sale' ? 'active' : ''}`}
                    onClick={() => setServiceType('sale')}
                  >
                    <Key className="service-option-icon" size={24} />
                    <span className="service-option-title">Araç Satın Alma</span>
                  </div>
                </div>
              </section>

              {/* Step 2: Vehicle Selection */}
              <section className="quote-section">
                <div className="quote-section-label">
                  <span className="quote-section-number">02</span>
                  <h3 className="quote-section-title">Araç Seçimi</h3>
                </div>
                <div className="vehicle-selection-grid">
                  {availableVehicles.map((vehicle) => (
                    <div 
                      key={vehicle.id}
                      className={`vehicle-mini-card ${selectedVehicle?.id === vehicle.id ? 'selected' : ''}`}
                      onClick={() => setSelectedVehicle(vehicle)}
                    >
                      <img src={vehicle.images[0]} alt={vehicle.title} className="vehicle-mini-img" />
                      <div className="vehicle-mini-name">{vehicle.title}</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Step 3: Information */}
              <section className="quote-section">
                <div className="quote-section-label">
                  <span className="quote-section-number">03</span>
                  <h3 className="quote-section-title">Bilgileriniz</h3>
                </div>
                
                <div className="quote-form-row">
                  <QuoteInput 
                    label="Ad Soyad" 
                    name="fullName" 
                    value={formData.fullName} 
                    onChange={handleInputChange} 
                  />
                  <QuoteInput 
                    label="E-Posta" 
                    name="email" 
                    type="email"
                    value={formData.email} 
                    onChange={handleInputChange} 
                  />
                </div>

                <div className="quote-form-row">
                  <QuoteInput 
                    label="Telefon" 
                    name="phone" 
                    value={formData.phone} 
                    onChange={handleInputChange} 
                    placeholder="+90 5xx xxx xx xx"
                  />
                  {serviceType === 'rental' ? (
                    <QuoteInput 
                      label="Kiralama Süresi" 
                      name="duration" 
                      value={formData.duration} 
                      onChange={handleInputChange} 
                      placeholder="Örn: 3 Gün"
                    />
                  ) : (
                    <QuoteInput 
                      label="Bütçe Aralığı" 
                      name="duration" 
                      value={formData.duration} 
                      onChange={handleInputChange} 
                      placeholder="Örn: 2M - 2.5M TL"
                    />
                  )}
                </div>

                <div className="quote-input-group" style={{ marginTop: '24px' }}>
                  <textarea 
                    name="notes"
                    className="quote-input" 
                    style={{ minHeight: '100px' }}
                    placeholder=" "
                    value={formData.notes}
                    onChange={handleInputChange}
                  ></textarea>
                  <label className="quote-input-label">Ek Notlar (Opsiyonel)</label>
                </div>
              </section>
            </motion.div>
          </div>

          {/* Sidebar Summary */}
          <aside className="quote-sidebar">
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="quote-summary"
            >
              <div className="quote-summary-card">
                <h4 className="summary-title">Özet</h4>
                
                <div className="summary-item">
                  <span className="summary-label">İŞLEM</span>
                  <span className="summary-value">
                    {serviceType === 'rental' ? 'Kiralama' : 'Satın Alma'}
                  </span>
                </div>

                <div className="summary-item">
                  <span className="summary-label">ARAÇ</span>
                  <AnimatePresence mode="wait">
                    <motion.span 
                      key={selectedVehicle?.id || 'none'}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      className="summary-value"
                      style={{ color: selectedVehicle ? 'var(--primary)' : 'var(--text-dim)' }}
                    >
                      {selectedVehicle ? selectedVehicle.title : 'Seçilmedi'}
                    </motion.span>
                  </AnimatePresence>
                </div>

                <div className="summary-item">
                  <span className="summary-label">KONUM</span>
                  <span className="summary-value">Kahramanmaraş</span>
                </div>

                <div style={{ margin: '20px 0', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <input 
                    type="checkbox" 
                    id="kvkk" 
                    name="kvkkAccepted"
                    checked={formData.kvkkAccepted}
                    onChange={handleInputChange}
                    style={{ marginTop: '4px', cursor: 'pointer' }}
                    required 
                  />
                  <label htmlFor="kvkk" style={{ fontSize: '0.8rem', color: 'var(--text-dim)', cursor: 'pointer', lineHeight: '1.4' }}>
                    <Link to="/kvkk" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>KVKK Metnini</Link> okudum ve verilerimin işlenmesini kabul ediyorum. *
                  </label>
                </div>

                <button type="submit" className="submit-quote-btn" disabled={loading}>
                  {loading ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <>
                      <span>Teklif Oluştur</span>
                      <Send size={18} />
                    </>
                  )}
                </button>

                <div className="quote-disclaimer">
                  <Shield size={14} style={{ marginBottom: '4px', display: 'inline' }} />
                  <p>
                    Verileriniz <Link to="/kvkk" style={{ color: 'var(--primary)', textDecoration: 'underline' }}>KVKK</Link> kapsamında yüksek güvenlik standartları ile korunmaktadır. Teklifiniz hazırlandığında size bilgi verilecektir.
                  </p>
                </div>
              </div>
            </motion.div>
          </aside>

        </form>
      </main>

      <Footer />
    </div>
  );
};

export default Quote;
